"use client"

import { useEffect, useRef, useState } from "react"
import Link from "next/link"
import { AlertTriangle, Check } from "lucide-react"
import { ToolPageShell, ToolProductCarousel, ToolShareBar } from "@/components/brand-kit"
import { GuideTool } from "@/components/tools/guide-tool"

// USDA / FDA food-safety rules — source: https://www.fsis.usda.gov/food-safety/safe-food-handling-and-preparation/food-safety-basics/keep-food-safe-food-safety-basics
//   • Refrigerator stays safe ~4 hours if door stays closed
//   • Full freezer stays safe ~48 hours; half-full ~24 hours, both door-closed
//   • Discard perishables held above 40°F for more than 2 hours

const SECTIONS = [
  { id: "rules", t: "USDA rules" },
  { id: "fridge", t: "What's in the fridge" },
  { id: "freezer", t: "What's in the freezer" },
  { id: "refreeze", t: "Can you refreeze?" },
  { id: "prep", t: "Before the next outage" },
] as const

export function PowerOutageFoodSafetyGuidePageClient() {
  const [active, setActive] = useState<string>(SECTIONS[0].id)
  const observerRef = useRef<IntersectionObserver | null>(null)

  useEffect(() => {
    if (observerRef.current) observerRef.current.disconnect()
    observerRef.current = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting)
        if (visible.length > 0) {
          const topmost = visible.reduce((prev, curr) =>
            prev.boundingClientRect.top < curr.boundingClientRect.top ? prev : curr,
          )
          setActive(topmost.target.id)
        }
      },
      { rootMargin: "-30% 0px -60% 0px", threshold: 0 },
    )
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id)
      if (el) observerRef.current?.observe(el)
    })
    return () => observerRef.current?.disconnect()
  }, [])

  const jumpTo = (id: string) => {
    const el = document.getElementById(id)
    if (!el) return
    const y = el.getBoundingClientRect().top + window.scrollY - 96
    window.scrollTo({ top: y, behavior: "smooth" })
    setActive(id)
    // Keep the URL hash in sync so the entry is a real, shareable anchor
    // (plan §0 item 179a). replaceState avoids the native hash-jump.
    history.replaceState(null, "", `#${id}`)
  }

  return (
    <ToolPageShell
      toolName="Power Outage Food Safety Guide"
      heroEyebrow="Food safety"
      heroTitlePre="The power's out."
      heroTitleItalic="What's still safe"
      heroTitlePost="to eat?"
      heroSubtitle="USDA's answer is exact: 4 hours for the fridge, 48 hours for a full freezer — door closed. Here's the food-by-food breakdown plus how to read what's safe vs what to discard."
      heroMeta={["⏱ ~4 min read", "USDA Food Safety guidance"]}
      heroShare={<ToolShareBar title="What's still safe to eat after a power outage?" />}
      heroRight={
        <div className="rounded-xl p-6 bg-gray-900 text-white">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-4 text-[#ff9951]">
            The USDA rule
          </div>
          <p
            className="text-[17px] font-medium leading-[1.45] mb-5"
            style={{ textWrap: "balance" as never }}
          >
            Keep doors{" "}
            <em className="italic text-[#ff9951]">closed</em>. Fridge stays safe{" "}
            <em className="italic text-[#ff9951]">4 hours</em>, full freezer{" "}
            <em className="italic text-[#ff9951]">48 hours</em>.
          </p>
          <div className="grid grid-cols-2 gap-3 pt-4 border-t border-white/10">
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Fridge
              </div>
              <div className="text-[22px] font-black tracking-tight">4 hrs</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.12em] text-white/50 font-bold">
                Full freezer
              </div>
              <div className="text-[22px] font-black tracking-tight">48 hrs</div>
            </div>
          </div>
        </div>
      }
      faqs={[
        {
          question: "What if I don't know how long the power was out?",
          answer:
            "Use an appliance thermometer. If the fridge is still at or below 40°F, the food is safe. For frozen food, USDA's rule is: if it still has ice crystals or is at or below 40°F, it's safe to refreeze — though quality may suffer.",
        },
        {
          question: "Can I taste food to check if it's safe?",
          answer:
            "No. Bacterial pathogens like Salmonella, E. coli, and Listeria don't change taste, smell, or appearance. USDA is unambiguous on this — 'when in doubt, throw it out' is the rule.",
        },
        {
          question: "Is a half-full freezer really only 24 hours?",
          answer:
            "Yes. Mass = thermal inertia. A full freezer holds the cold longer because there's more frozen mass per cubic foot. If you live in an outage-prone area, keep your freezer at least 75% full year-round.",
        },
        {
          question: "What about food in coolers with ice?",
          answer:
            "Treat it like the freezer: as long as the temperature stays at or below 40°F (use a thermometer), it's safe. Block ice and dry ice both extend cooler life — dry ice can keep an 18 cu ft freezer cold for ~2 days at 50 lbs.",
        },
        {
          question: "How fast does the fridge actually warm up?",
          answer:
            "Roughly 1–2°F per hour with the door closed at typical kitchen temperatures, faster in a hot summer kitchen. The 4-hour USDA rule is intentionally conservative — assume you've crossed 40°F at hour 4 unless a thermometer says otherwise.",
        },
      ]}
      relatedTools={[
        {
          title: "Power Outage Center",
          category: "Storm Center",
          description: "Food-by-food keep-or-toss triage + the printable fridge-door checklist.",
          href: "/tools/power-outage-center",
          time: "~60 sec",
        },
        {
          title: "Dry Ice Calculator",
          category: "Food Storage",
          description: "How much dry ice for your freezer.",
          href: "/tools/freezer-dry-ice-calculator",
          time: "~30 sec",
        },
        {
          title: "Generator Size Calculator",
          category: "Energy & Cost",
          description: "Watts needed to run your fridge.",
          href: "/tools/refrigerator-generator-size-calculator",
          time: "~45 sec",
        },
        {
          title: "Food Storage Calculator",
          category: "Food Storage",
          description: "Shelf life by food category.",
          href: "/tools/food-storage-calculator",
          time: "~15 sec",
        },
      ]}
      cta={{
        title: "Outage-prone area?",
        body: "A bigger freezer holds the cold longer — the right model lets you sleep through 48-hour outages without losing a thing.",
        buttonLabel: "Browse all freezers",
        buttonHref: "/best-freezers",
      }}
    >
      {/* ============ ARTICLE BODY WITH STICKY TOC ============ */}
      <section className="py-8 md:py-12 bg-white">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 grid md:grid-cols-[170px_1fr] gap-8 md:gap-10">
          {/* Sticky TOC */}
          <aside className="hidden md:block">
            <div className="sticky top-[96px]">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">
                On this page
              </div>
              <nav className="border-l border-gray-200" aria-label="Section navigation">
                {SECTIONS.map((s) => {
                  const isActive = active === s.id
                  return (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      onClick={(e) => {
                        e.preventDefault()
                        jumpTo(s.id)
                      }}
                      aria-current={isActive ? "location" : undefined}
                      className={[
                        "block cursor-pointer py-2 pl-3 -ml-px transition-all text-left w-full",
                        isActive ? "text-gray-900" : "text-gray-500 hover:text-gray-700",
                      ].join(" ")}
                      style={{
                        borderLeft: isActive ? "2px solid #ff9951" : "2px solid transparent",
                      }}
                    >
                      <div className="text-[13px] font-bold leading-tight">{s.t}</div>
                    </a>
                  )
                })}
              </nav>
            </div>
          </aside>

          {/* Article body */}
          <article>
            <GuideTool
              eyebrow="Interactive keep-or-toss"
              title="Power's back \u2014 what's still safe?"
              intro="USDA's rules come down to hours, door discipline, and how full the freezer was. Answer three questions for the call."
              questions={[
                            {
                              id: "hours",
                              label: "How long was the power out?",
                              options: [
                                { value: "h4", label: "Under 4 hours" },
                                { value: "h24", label: "4\u201324 hours" },
                                { value: "h48", label: "24\u201348 hours" },
                                { value: "h48p", label: "Over 48 hours" }
                              ],
                            },
                            {
                              id: "door",
                              label: "Was the door opened during the outage?",
                              options: [
                                { value: "closed", label: "Kept closed" },
                                { value: "opened", label: "Opened a few times" }
                              ],
                            },
                            {
                              id: "what",
                              label: "What are you checking?",
                              options: [
                                { value: "fridge", label: "Refrigerator food" },
                                { value: "full", label: "Freezer \u2014 was full" },
                                { value: "half", label: "Freezer \u2014 half full" }
                              ],
                            }
              ]}
              compute={(a) => {
                            if (a.what === "fridge") {
                              if (a.hours === "h4" && a.door === "closed") {
                                return { tone: "good", verdict: "Refrigerator food is safe", detail: "USDA: an unopened refrigerator keeps food safe for about 4 hours. Move perishables to ice if the outage continues." }
                              }
                              return { tone: "bad", verdict: "Toss the perishables", detail: "Past 4 hours above 40\u00b0F (sooner with the door opened), USDA says discard meat, poultry, fish, eggs, dairy, and leftovers. When in doubt, throw it out \u2014 taste and smell do not detect the bacteria that matter." }
                            }
                            const cap = a.what === "full" ? 48 : 24
                            const hrs = a.hours === "h4" ? 4 : a.hours === "h24" ? 24 : a.hours === "h48" ? 48 : 72
                            const eff = a.door === "opened" ? hrs + 12 : hrs
                            if (eff <= cap) {
                              return { tone: "good", verdict: "Freezer food is safe", detail: "A " + (a.what === "full" ? "full" : "half-full") + " freezer holds safe temperature about " + cap + " hours unopened. Food with ice crystals \u2014 or at 40\u00b0F or below \u2014 can even be refrozen (quality may dip, safety holds)." }
                            }
                            return { tone: "warn", verdict: "Check item by item", detail: "Past the " + cap + "-hour window, go by state: anything still holding ice crystals or reading 40\u00b0F or below is safe to refreeze or cook; anything fully thawed and warmer than 40\u00b0F for over 2 hours goes." }
                          }}
              ctaLabel="Make the call"
              footnote="Rules: USDA FSIS \u2014 4 hours refrigerator, 24/48 hours half/full freezer."
            />
            {/* USDA rules */}
            <section id="rules" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                USDA&apos;s three rules
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Per USDA Food Safety and Inspection Service, three rules govern food safety during
                a power outage. Memorize them.
              </p>
              <ol className="list-none p-0 m-0 border-t border-gray-900">
                {[
                  {
                    n: "01",
                    t: "Keep doors closed",
                    d: "A closed refrigerator stays safe for ~4 hours. Each opening costs roughly 30 minutes of safe time. A closed full freezer stays safe ~48 hours; half-full, ~24 hours.",
                  },
                  {
                    n: "02",
                    t: "40°F is the danger line",
                    d: "Once perishables sit above 40°F for more than 2 hours, USDA says discard them. This applies to meat, poultry, fish, eggs, dairy, and leftovers — not to condiments, hard cheese, or whole produce.",
                  },
                  {
                    n: "03",
                    t: "Verify, don't taste",
                    d: "Use an appliance thermometer to confirm temps. Bacterial pathogens don't change taste or smell — tasting is not a safety check, ever.",
                  },
                ].map((s) => (
                  <li
                    key={s.n}
                    className="grid grid-cols-[auto_1fr] gap-5 sm:gap-7 items-start py-5 sm:py-6 border-b border-gray-900"
                  >
                    <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] pt-1 text-[#ff9951]">
                      {s.n}
                    </div>
                    <div>
                      <h3
                        className="text-gray-900"
                        style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}
                      >
                        {s.t}
                      </h3>
                      <p className="mt-1.5 text-[15px] text-gray-600 leading-[1.55]">{s.d}</p>
                    </div>
                  </li>
                ))}
              </ol>
            </section>

            {/* Fridge */}
            <section id="fridge" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What to do with fridge contents
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Group your fridge into three categories — discard, check, keep — based on USDA
                guidance.
              </p>
              <div className="overflow-x-auto rounded-xl border border-gray-900">
                <table className="w-full text-[14px]">
                  <thead>
                    <tr className="bg-gray-900 text-white">
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Category
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Verdict
                      </th>
                      <th className="px-5 py-3 text-left text-[11px] uppercase tracking-[0.15em] font-bold">
                        Examples
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      ["Raw meat, poultry, seafood", "Discard after 2 hrs above 40°F", "Ground beef, chicken, fish fillets"],
                      ["Cooked meat & leftovers", "Discard after 2 hrs above 40°F", "Casseroles, soups, cooked pasta"],
                      ["Dairy (soft)", "Discard after 2 hrs above 40°F", "Milk, cream, yogurt, soft cheese"],
                      ["Eggs & egg dishes", "Discard after 2 hrs above 40°F", "Fresh eggs, quiche, custards"],
                      ["Hard cheese", "Keep — generally safe", "Cheddar, Swiss, Parmesan"],
                      ["Butter & margarine", "Keep — generally safe", "Stick butter, soft margarine"],
                      ["Whole produce", "Keep — generally safe", "Apples, oranges, whole carrots"],
                      ["Cut produce", "Discard after 2 hrs above 40°F", "Sliced melon, cut salad greens"],
                      ["Condiments", "Keep (most)", "Ketchup, mustard, jam, pickles"],
                      ["Mayo & tartar sauce", "Discard after 8 hrs above 40°F", "Opened mayo jar, deli salads"],
                    ].map((r, i, arr) => (
                      <tr
                        key={r[0]}
                        className={i % 2 ? "bg-gray-50" : "bg-white"}
                        style={{ borderBottom: i < arr.length - 1 ? "1px solid #e5e7eb" : "none" }}
                      >
                        <td className="px-5 py-4 font-extrabold tracking-tight">{r[0]}</td>
                        <td className="px-5 py-4">
                          <span className="font-mono font-bold text-[#ff9951]">{r[1]}</span>
                        </td>
                        <td className="px-5 py-4 text-gray-700">{r[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[13px] text-gray-500 mt-4">
                Source: USDA Food Safety and Inspection Service, &ldquo;Food Safety During Power
                Outages.&rdquo;
              </p>
            </section>

            {/* Freezer */}
            <section id="freezer" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                What to do with frozen food
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                Frozen food is more forgiving than refrigerated. USDA&apos;s rule:{" "}
                <strong>if it still has ice crystals or is at or below 40°F, it&apos;s safe to refreeze.</strong>{" "}
                Quality may suffer, but safety is intact.
              </p>

              <div
                className="rounded-xl p-5 md:p-6 my-8 flex gap-4 md:gap-5"
                style={{ background: "rgba(255,153,81,0.06)", border: "1px solid rgba(255,153,81,0.25)" }}
              >
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-[#ff9951] text-[var(--color-brand-action-text)] flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-black text-[17px] mb-1 text-gray-900">
                    Full vs half-full freezer
                  </div>
                  <p className="text-[14px] text-gray-700 leading-[1.6]">
                    Per USDA: a full freezer stays safe ~48 hours; half-full, ~24 hours. Mass equals
                    thermal inertia — the more frozen food packed inside, the longer it holds. Keep
                    your freezer at least 75% full year-round if outages are common.
                  </p>
                </div>
              </div>
            </section>

            {/* Refreeze */}
            <section id="refreeze" className="mb-12 md:mb-14 scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Can you refreeze it?
              </h2>
              <p className="text-[17px] text-gray-700 leading-[1.7] mb-6">
                USDA&apos;s three-part test:
              </p>
              <div className="space-y-3">
                {[
                  {
                    t: "Still has ice crystals",
                    d: "Yes, refreeze. The food never fully thawed — it's safe.",
                  },
                  {
                    t: "Thawed but still cold (≤40°F)",
                    d: "Refreeze for safety, but use sooner — quality and texture will suffer. Cook before refreezing where practical.",
                  },
                  {
                    t: "Above 40°F for over 2 hours",
                    d: "Discard. Bacterial growth has crossed USDA's safety line.",
                  },
                ].map((r, i) => (
                  <div
                    key={r.t}
                    className="grid grid-cols-[36px_1fr] sm:grid-cols-[44px_1fr] gap-4 sm:gap-5 p-4 sm:p-5 rounded-lg"
                    style={{ background: i === 0 ? "rgba(16,166,92,0.06)" : i === 2 ? "rgba(220,38,38,0.06)" : "#f9fafb" }}
                  >
                    <div
                      className="font-mono font-bold"
                      style={{
                        fontSize: 14,
                        letterSpacing: "0.05em",
                        color: i === 0 ? "#10a65c" : i === 2 ? "#dc2626" : "#9ca3af",
                        lineHeight: 1.4,
                      }}
                    >
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <div>
                      <h3 className="text-[18px] font-extrabold tracking-[-0.015em] mb-1 text-gray-900">
                        {r.t}
                      </h3>
                      <p className="text-[14px] text-gray-600 leading-[1.55]">{r.d}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Prep */}
            <section id="prep" className="scroll-mt-24">
              <h2
                className="font-black text-gray-900 m-0 mb-5 pb-3 border-b border-gray-900"
                style={{ fontSize: "clamp(22px, 2.6vw, 28px)", letterSpacing: "-0.02em" }}
              >
                Before the next outage
              </h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  "Keep an appliance thermometer in fridge and freezer",
                  "Freeze water containers in any spare freezer space",
                  "Group frozen items together for thermal mass",
                  "Keep freezer 75%+ full year-round",
                  "Have block ice or dry ice contact info ready",
                  "Know where the nearest 24-hr ice supply is",
                  "Keep a cooler on hand (50-qt minimum)",
                  "Save USDA's outage page on your phone",
                ].map((t) => (
                  <div key={t} className="flex items-start gap-3 p-4 rounded-lg bg-gray-50">
                    <div className="w-6 h-6 rounded-full bg-[#0469f0] text-white flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3.5 h-3.5" />
                    </div>
                    <span className="text-[14px] text-gray-700 leading-[1.5]">{t}</span>
                  </div>
                ))}
              </div>
              <div className="mt-4 p-5 rounded-xl border border-[#ff9951]/40 bg-[#ff9951]/5">
                <p className="text-[14px] text-gray-800 leading-[1.6] m-0">
                  <strong>When the power actually goes out:</strong> the{" "}
                  <Link href="/tools/power-outage-center" className="font-bold underline text-[#0469f0]">
                    Power Outage Center
                  </Link>{" "}
                  turns these rules into food-by-food keep-or-toss verdicts and a printable
                  fridge-door checklist — tape it up before the next storm.
                </p>
              </div>
            </section>
          </article>
        </div>
      </section>

      {/* ============ PRIMARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Outage-resilient picks"
          title="French door fridges with deeper insulation"
          description="Newer ENERGY STAR units hold cold ~30% longer during outages."
          collectionType="french-door"
          fallbackCollectionType="bottom-freezer"
        />
      </section>

      {/* ============ SECONDARY CAROUSEL ============ */}
      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Upright freezers for outage prep"
          description="A full upright freezer holds 48 hours of safe storage with the door closed."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
